import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Lock, Plus, Trash2, Edit, Eye, Mail, MessageSquare, BarChart3, LogOut, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  "Agentic AI",
  "Contact Center AI",
  "Enterprise AI Strategy",
  "Tutorials",
  "Leadership Insights"
];

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [adminToken, setAdminToken] = useState("");
  const [stats, setStats] = useState(null);
  const [articles, setArticles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [isLoading, setIsLoading] = useState(false);
  
  // Article form state
  const [articleForm, setArticleForm] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    category: "",
    tags: "",
    is_newsletter: false,
    external_link: "",
    is_published: true
  });
  const [editingArticle, setEditingArticle] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setAdminToken(response.data.token);
        setIsAuthenticated(true);
        toast.success("Logged in successfully");
        fetchData(response.data.token);
      }
    } catch (error) {
      toast.error("Invalid password");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAdminToken("");
    setPassword("");
    toast.success("Logged out successfully");
  };

  const fetchData = async (token) => {
    const headers = { "x-admin-token": token };
    try {
      const [statsRes, articlesRes, messagesRes, subscribersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/articles`),
        axios.get(`${API}/contact/messages`, { headers }),
        axios.get(`${API}/newsletter/subscribers`, { headers })
      ]);
      setStats(statsRes.data);
      setArticles(articlesRes.data);
      setMessages(messagesRes.data);
      setSubscribers(subscribersRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleArticleFormChange = (e) => {
    const { name, value } = e.target;
    setArticleForm(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'title') {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  };

  const handleSubmitArticle = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    const headers = { "x-admin-token": adminToken };
    const payload = {
      ...articleForm,
      tags: articleForm.tags.split(',').map(t => t.trim()).filter(Boolean)
    };

    try {
      if (editingArticle) {
        await axios.put(`${API}/articles/${editingArticle}`, payload, { headers });
        toast.success("Article updated successfully");
      } else {
        await axios.post(`${API}/articles`, payload, { headers });
        toast.success("Article created successfully");
      }
      
      setArticleForm({
        title: "",
        slug: "",
        excerpt: "",
        content: "",
        category: "",
        tags: "",
        is_newsletter: false,
        external_link: "",
        is_published: true
      });
      setEditingArticle(null);
      fetchData(adminToken);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to save article";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteArticle = async (slug) => {
    if (!window.confirm("Are you sure you want to delete this article?")) return;
    
    try {
      await axios.delete(`${API}/articles/${slug}`, {
        headers: { "x-admin-token": adminToken }
      });
      toast.success("Article deleted");
      fetchData(adminToken);
    } catch (error) {
      toast.error("Failed to delete article");
    }
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/contact/messages/${messageId}/read`, {}, {
        headers: { "x-admin-token": adminToken }
      });
      fetchData(adminToken);
    } catch (error) {
      toast.error("Failed to mark as read");
    }
  };

  const handleSendNewsletter = async (articleSlug, articleTitle) => {
    if (!window.confirm(`Send "${articleTitle}" to all ${stats?.subscribers || 0} subscribers?`)) return;
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/newsletter/send`, 
        { article_slug: articleSlug },
        { headers: { "x-admin-token": adminToken } }
      );
      toast.success(`Newsletter sent! ${response.data.sent} delivered, ${response.data.failed} failed`);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send newsletter";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendTestNewsletter = async (articleSlug, articleTitle) => {
    const testEmail = window.prompt(`Send test email for "${articleTitle}" to:`, "vasan07.natarajan@gmail.com");
    if (!testEmail) return;
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/newsletter/send-test`, 
        { article_slug: articleSlug, test_email: testEmail },
        { headers: { "x-admin-token": adminToken } }
      );
      toast.success(`Test email sent to ${testEmail}!`);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send test email";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-[#FAFAFA]" data-testid="admin-login-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md p-8"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-royal-blue-800 text-white rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock size={32} />
            </div>
            <h1 className="font-display text-2xl font-semibold text-zinc-950">Admin Access</h1>
            <p className="text-zinc-600 mt-2">Enter your password to continue</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="bg-white"
                data-testid="admin-password-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-royal-blue-800 hover:bg-royal-blue-700"
              data-testid="admin-login-btn"
            >
              Login
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[#FAFAFA]" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-display text-3xl font-semibold text-zinc-950">Admin Dashboard</h1>
            <p className="text-zinc-600">Manage your content and subscribers</p>
          </div>
          <Button 
            variant="outline" 
            onClick={handleLogout}
            data-testid="admin-logout-btn"
          >
            <LogOut size={16} className="mr-2" />
            Logout
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card data-testid="stat-subscribers">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-full">
                    <Mail className="text-royal-blue-800" size={24} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-zinc-950">{stats.subscribers}</p>
                    <p className="text-sm text-zinc-600">Subscribers</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card data-testid="stat-articles">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-full">
                    <BarChart3 className="text-green-600" size={24} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-zinc-950">{stats.articles}</p>
                    <p className="text-sm text-zinc-600">Articles</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card data-testid="stat-newsletters">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 rounded-full">
                    <Mail className="text-purple-600" size={24} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-zinc-950">{stats.newsletters}</p>
                    <p className="text-sm text-zinc-600">Newsletters</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card data-testid="stat-messages">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-orange-100 rounded-full">
                    <MessageSquare className="text-orange-600" size={24} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-zinc-950">{stats.unread_messages}</p>
                    <p className="text-sm text-zinc-600">Unread Messages</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-zinc-200 pb-4">
          {["dashboard", "articles", "messages", "subscribers"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                activeTab === tab
                  ? "bg-royal-blue-800 text-white"
                  : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "dashboard" && (
          <div className="space-y-8">
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="flex gap-4">
                <Button 
                  onClick={() => setActiveTab("articles")}
                  className="bg-royal-blue-800 hover:bg-royal-blue-700"
                  data-testid="quick-new-article"
                >
                  <Plus size={16} className="mr-2" />
                  New Article
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "articles" && (
          <div className="space-y-8">
            {/* Article Form */}
            <Card>
              <CardHeader>
                <CardTitle>{editingArticle ? "Edit Article" : "Create New Article"}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmitArticle} className="space-y-6" data-testid="article-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="title">Title</Label>
                      <Input
                        id="title"
                        name="title"
                        value={articleForm.title}
                        onChange={handleArticleFormChange}
                        placeholder="Article title"
                        data-testid="article-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="slug">Slug</Label>
                      <Input
                        id="slug"
                        name="slug"
                        value={articleForm.slug}
                        onChange={handleArticleFormChange}
                        placeholder="article-slug"
                        data-testid="article-slug-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="excerpt">Excerpt</Label>
                    <Textarea
                      id="excerpt"
                      name="excerpt"
                      value={articleForm.excerpt}
                      onChange={handleArticleFormChange}
                      placeholder="Brief description..."
                      rows={2}
                      data-testid="article-excerpt-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="content">Content</Label>
                    <div className="text-xs text-zinc-500 mb-2 p-3 bg-zinc-50 rounded border">
                      <p className="font-semibold mb-1">Formatting Tips:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Use blank lines between paragraphs</li>
                        <li>Start lines with <code className="bg-zinc-200 px-1">-</code> or <code className="bg-zinc-200 px-1">•</code> for bullet points</li>
                        <li>Start lines with <code className="bg-zinc-200 px-1">1.</code> for numbered lists</li>
                        <li>Use <code className="bg-zinc-200 px-1">## Heading</code> for section headers</li>
                      </ul>
                    </div>
                    <Textarea
                      id="content"
                      name="content"
                      value={articleForm.content}
                      onChange={handleArticleFormChange}
                      placeholder="## Introduction

Write your opening paragraph here. Use blank lines to separate sections.

## Key Highlights

- First important point
- Second important point  
- Third important point

## Deep Dive

Add more detailed content here. Each paragraph separated by a blank line will be formatted properly.

## Conclusion

Wrap up your newsletter with final thoughts."
                      rows={16}
                      data-testid="article-content-input"
                      className="font-mono text-sm"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="category">Category</Label>
                      <Select
                        value={articleForm.category}
                        onValueChange={(value) => setArticleForm(prev => ({ ...prev, category: value }))}
                      >
                        <SelectTrigger data-testid="article-category-select">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tags">Tags (comma-separated)</Label>
                      <Input
                        id="tags"
                        name="tags"
                        value={articleForm.tags}
                        onChange={handleArticleFormChange}
                        placeholder="AI, Strategy, LLM"
                        data-testid="article-tags-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="external_link">External Link (optional)</Label>
                    <Input
                      id="external_link"
                      name="external_link"
                      value={articleForm.external_link}
                      onChange={handleArticleFormChange}
                      placeholder="https://linkedin.com/..."
                      data-testid="article-external-link-input"
                    />
                  </div>
                  
                  <div className="flex items-center gap-8">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="is_newsletter"
                        checked={articleForm.is_newsletter}
                        onCheckedChange={(checked) => setArticleForm(prev => ({ ...prev, is_newsletter: checked }))}
                        data-testid="article-newsletter-switch"
                      />
                      <Label htmlFor="is_newsletter">Newsletter Edition</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        id="is_published"
                        checked={articleForm.is_published}
                        onCheckedChange={(checked) => setArticleForm(prev => ({ ...prev, is_published: checked }))}
                        data-testid="article-published-switch"
                      />
                      <Label htmlFor="is_published">Published</Label>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <Button 
                      type="submit" 
                      disabled={isLoading}
                      className="bg-royal-blue-800 hover:bg-royal-blue-700"
                      data-testid="article-submit-btn"
                    >
                      {isLoading ? "Saving..." : (editingArticle ? "Update Article" : "Create Article")}
                    </Button>
                    {editingArticle && (
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => {
                          setEditingArticle(null);
                          setArticleForm({
                            title: "",
                            slug: "",
                            excerpt: "",
                            content: "",
                            category: "",
                            tags: "",
                            is_newsletter: false,
                            external_link: "",
                            is_published: true
                          });
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Articles List */}
            <Card>
              <CardHeader>
                <CardTitle>Articles ({articles.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {articles.map((article, index) => (
                    <div 
                      key={article.id} 
                      className="flex items-center justify-between p-4 bg-zinc-50 border border-zinc-200 rounded-sm"
                      data-testid={`article-item-${index}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-zinc-950">{article.title}</h3>
                          {article.is_newsletter && (
                            <Badge variant="secondary">Newsletter</Badge>
                          )}
                        </div>
                        <p className="text-sm text-zinc-600">{article.category}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          title="Send Test Email"
                          onClick={() => handleSendTestNewsletter(article.slug, article.title)}
                          disabled={isLoading}
                        >
                          <FlaskConical size={16} className="text-amber-600" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          title="Send to All Subscribers"
                          onClick={() => handleSendNewsletter(article.slug, article.title)}
                          disabled={isLoading}
                        >
                          <Mail size={16} className="text-royal-blue-800" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => window.open(`/blog/${article.slug}`, '_blank')}
                        >
                          <Eye size={16} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setEditingArticle(article.slug || article.id);
                            setArticleForm({
                              ...article,
                              tags: article.tags ? article.tags.join(', ') : ''
                            });
                          }}
                          title="Edit article"
                        >
                          <Edit size={16} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteArticle(article.slug || article.id)}
                          title={`Delete article (ID: ${article.id})`}
                        >
                          <Trash2 size={16} className="text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {articles.length === 0 && (
                    <p className="text-center text-zinc-500 py-8">No articles yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "messages" && (
          <Card>
            <CardHeader>
              <CardTitle>Contact Messages ({messages.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <div 
                    key={message.id}
                    className={`p-4 border rounded-sm ${message.is_read ? 'bg-white border-zinc-200' : 'bg-blue-50 border-blue-200'}`}
                    data-testid={`message-item-${index}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold text-zinc-950">{message.name}</h3>
                        <p className="text-sm text-zinc-600">{message.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!message.is_read && (
                          <Badge variant="secondary" className="bg-blue-100 text-blue-800">New</Badge>
                        )}
                        <span className="text-xs text-zinc-500">
                          {new Date(message.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <p className="font-medium text-zinc-800 mb-2">{message.subject}</p>
                    <p className="text-zinc-600 text-sm">{message.message}</p>
                    {!message.is_read && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-4"
                        onClick={() => handleMarkAsRead(message.id)}
                      >
                        Mark as Read
                      </Button>
                    )}
                  </div>
                ))}
                {messages.length === 0 && (
                  <p className="text-center text-zinc-500 py-8">No messages yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === "subscribers" && (
          <Card>
            <CardHeader>
              <CardTitle>Newsletter Subscribers ({subscribers.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {subscribers.map((subscriber, index) => (
                  <div 
                    key={subscriber.id}
                    className="flex items-center justify-between p-3 bg-zinc-50 border border-zinc-200 rounded-sm"
                    data-testid={`subscriber-item-${index}`}
                  >
                    <span className="text-zinc-700">{subscriber.email}</span>
                    <span className="text-xs text-zinc-500">
                      {new Date(subscriber.subscribed_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
                {subscribers.length === 0 && (
                  <p className="text-center text-zinc-500 py-8">No subscribers yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
